//@flow
import React from 'react';
import {useHistory, useParams, Link} from 'react-router-dom';
import {
  makeStyles,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
} from '@material-ui/core';
import {ArrowBack, Delete, Edit, GetApp} from '@material-ui/icons';
import {
  useCrmCampaign,
  useCrmCampaignsActions,
  useCrmCampaignExecution,
} from '../../utils';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';
import {colors} from '../../../../assets/styles/colors';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {useCrmSegments} from '../../../crm-segment/utils';
import CustomChip from '../../../../elements/CustomChip';
import {formatDateTime, getStringDate} from '../../../../utils/date';
import {
  CAMPAIGN_EXECUTION_STATE_LABELS,
  CAMPAIGN_EXECUTION_STATE_COLORS,
} from '../../constants';
import {Pagination} from '@material-ui/lab';
import {getCampaignExecutionByIdUrl} from '../../promises';
import {ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM} from '../crm-campaign-form';

const useStyles = makeStyles(theme => ({
  pageTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  container: {
    width: '100%',
    display: 'flex',
    padding: '1rem',
    flexDirection: 'column',
    gap: '1rem',
    position: 'relative',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '.5rem',
    maxWidth: 760,
  },

  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  root: {
    width: '100%',
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  header: {
    borderRadius: '.5rem',
    background: 'white',
    padding: '1rem',
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    maxWidth: 760,
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '1rem',
  },
  campaignData: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  campaignDataItem: {
    fontSize: '14px',
    fontWeight: 400,
    color: colors.grey600,
  },
  contactSummary: {
    background: 'white',
    padding: '1rem',
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    borderRadius: '.5rem',
  },
  summaryCardGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: '1fr 1fr',

    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
    },
  },
  summaryCard: {
    padding: '1rem 2rem',
    background: colors.grey100,
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey200}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '.5rem',
  },
  subTitle: {
    fontSize: '16px',
    fontWeight: 600,
  },
  tableContainer: {
    marginBlock: '.5rem',
    width: '100%',
    borderRadius: '.5rem',
    overflow: 'hidden',
    height: 'auto',
    border: `1px solid ${colors.grey300}`,
  },
  tableHeaderText: {
    fontSize: '14px',
    fontWeight: 600,
  },
  deleteButton: {
    color: colors.error,
    width: 'fit-content',
  },
  editButton: {
    color: colors.primary,
    width: 'fit-content',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '.25rem',
  },
  paginationArea: {
    padding: '1rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default function CampaignDetail() {
  const classes = useStyles();

  const {id} = useParams();

  const history = useHistory();

  const {campaign, loading: gettingCampaigns} = useCrmCampaign(id ?? '');
  const {
    deleteCrmCampaign,
    loading: deletingCampaigns,
  } = useCrmCampaignsActions();

  const {executions, page, pagination, setPage} = useCrmCampaignExecution(
    id ?? ''
  );

  const {loading: loadingSegments, segments} = useCrmSegments({
    page: 0,
    size: 30,
  });

  if (!campaign) {
    return null;
  }

  const loading = gettingCampaigns || deletingCampaigns || loadingSegments;
  const segment = segments.find(segment => segment.id === campaign.segmentId);

  const handleDelete = () => {
    deleteCrmCampaign(campaign.id).then(() => {
      history.push('/campaigns');
    });
  };

  return (
    <div className={classes.container}>
      <header className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>
          <IconButton
            className={classes.backButton}
            onClick={() => {
              history.push('/campaigns');
            }}
          >
            <ArrowBack />
          </IconButton>
          Detalle de la Campaña
        </Typography>
        <div>
          <CustomButton
            className={classes.editButton}
            variant="text"
            startIcon={<Edit />}
            component={Link}
            to={`/campaigns/${campaign.id}${
              campaign.ravenService === 'WHATSAPP_WEB'
                ? `?${ENABLE_WHATSAPP_WEB_PROVIDER_SEARCH_PARAM}`
                : ''
            }`}
          >
            Editar
          </CustomButton>
          <CustomButton
            className={classes.deleteButton}
            variant="text"
            startIcon={<Delete />}
            onClick={handleDelete}
          >
            Eliminar
          </CustomButton>
        </div>
      </header>
      <aside className={classes.root}>
        {loading && (
          <div className={classes.loader}>
            <InlineLoader loading={loading} />
          </div>
        )}
        <header className={classes.header}>
          <Typography className={classes.title}>{campaign.name}</Typography>

          <Typography variant="body2">
            <strong>Segmento:</strong> {segment?.name}
          </Typography>

          <Typography variant="body2">
            <strong>Estado:</strong>{' '}
            <CustomChip
              size="small"
              color={campaign.state === 'ACTIVE' ? 'primary' : 'warning'}
              label={campaign.state === 'ACTIVE' ? 'Activo' : 'Borrador'}
            />
          </Typography>
          <div className={classes.campaignData}>
            {/*  <Typography className={classes.campaignDataItem}>
              Contactos: 80
            </Typography> */}
            <Typography className={classes.campaignDataItem}>
              Canal de envío: {campaign.ravenService}
            </Typography>
            {/*  <Typography className={classes.campaignDataItem}>
              Plantilla de Whatsapp: Octubre Joyas
            </Typography> */}
            {/* 
            <Typography className={classes.campaignDataItem}>
              Fecha de creación: 08/10/2024
            </Typography> */}
            {campaign && segment?.segmentType === 'MANUAL' && (
              <Typography className={classes.campaignDataItem}>
                Fecha de envío: {formatDateTime(new Date(campaign.scheduledAt))}
              </Typography>
            )}
          </div>
        </header>

        {executions.length > 0 && (
          <div className={classes.contactSummary}>
            <Typography className={classes.subTitle}>
              Registro de envíos
            </Typography>

            <div className={classes.tableContainer}>
              <Table>
                <TableHead className={classes.tableHeader}>
                  <TableCell />
                  <TableCell>
                    <Typography className={classes.tableHeaderText}>
                      Estado
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.tableHeaderText}>
                      Fecha programada
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.tableHeaderText}>
                      Fecha de envío
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography className={classes.tableHeaderText}>
                      Resumen
                    </Typography>
                  </TableCell>
                </TableHead>

                <TableBody className={classes.tableBody}>
                  {executions.map(item => (
                    <TableRow key={item.id} className={classes.tableRow}>
                      <TableCell padding="checkbox">
                        <IconButton
                          color="primary"
                          component="a"
                          href={getCampaignExecutionByIdUrl(item.id)}
                          target="_blank"
                        >
                          <GetApp />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <CustomChip
                          color={CAMPAIGN_EXECUTION_STATE_COLORS[item.state]}
                          size="small"
                          label={CAMPAIGN_EXECUTION_STATE_LABELS[item.state]}
                        />
                      </TableCell>
                      <TableCell>{getStringDate(item.scheduledAt)}</TableCell>
                      <TableCell>
                        {item.executedAt
                          ? getStringDate(item.executedAt)
                          : 'No enviado'}
                      </TableCell>
                      <TableCell>
                        <div className={classes.summaryRow}>
                          {/*                           <span>{item.detail.sent} Enviados</span>
                          <span>{item.detail.read} Leídos</span>
                          <span>{item.detail.failed} Fallidos</span> */}
                          <CustomChip
                            color="primary"
                            size="small"
                            label={`${item.detail.sent} Enviados`}
                          />

                          <CustomChip
                            color="success"
                            size="small"
                            label={`${item.detail.read} Leídos`}
                          />

                          <CustomChip
                            color="danger"
                            size="small"
                            label={`${item.detail.failed} Fallidos`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className={classes.paginationArea}>
                <Pagination
                  count={
                    pagination &&
                    (pagination.size >= pagination.total
                      ? 1
                      : Math.ceil(pagination.total / pagination.size))
                  }
                  className={classes.pagination}
                  color="primary"
                  page={page + 1}
                  onChange={(e, page) => {
                    setPage(page - 1);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {executions.length === 0 && (
          <div className={classes.contactSummary}>
            <Typography className={classes.subTitle}>
              No hay ejecuciones registradas
            </Typography>
          </div>
        )}
      </aside>
    </div>
  );
}
